// src/context/BoosterNFTContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import { ethers } from 'ethers';
import { WalletContext } from './WalletContext';
import CashBoosterNFTABI from '../abi/CashBoosterNFT.json';
import { BOOSTERNFT_ADDRESS, NETWORK_CONFIG } from '../contracts/constants';
import { toast } from 'react-toastify';

export const BoosterNFTContext = createContext();

export const BoosterNFTProvider = ({ children }) => {
  // Use the provider (which should be a Web3Provider from ethers v5) from WalletContext
  const { account, provider, chainId } = useContext(WalletContext);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [boosterNFTs, setBoosterNFTs] = useState([]);
  const [isFetchingNFTs, setIsFetchingNFTs] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalBoostPercentage, setTotalBoostPercentage] = useState(0);

  const boosterNftContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const initializeRetryCountRef = useRef(0);
  const lastFetchTimeRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const MAX_INITIALIZE_ATTEMPTS = 3;

  // Retry helper for contract calls
  const retryContractCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        if (
          error.code === -32603 ||
          error?.data?.code === -32094 ||
          (error?.data?.message || "").includes("Blockchain height exceeded")
        ) {
          if (i < maxAttempts - 1) {
            const delay = Math.pow(2, i) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
        }
        console.error("Contract call failed after retries:", error);
        throw error;
      }
    }
    return null;
  }, []);

  // Fetch user's Booster NFTs
  const fetchUserBoosterNFTs = useCallback(
    async (isInitial = false) => {
      if (!account || !boosterNftContractRef.current) return;
      if (!isInitial && isFetchingNFTs) {
        return;
      }
      const now = Date.now();
      if (now - lastFetchTimeRef.current < 10000 && !isInitial) {
        return;
      }
      lastFetchTimeRef.current = now;

      setIsFetchingNFTs(true);
      setIsLoading(true);
      try {
        const contract = boosterNftContractRef.current;
        const balanceRaw = await retryContractCall(() => contract.balanceOf(account));
        const balance = balanceRaw.toNumber();

        if (balance === 0) {
          setBoosterNFTs([]);
          setIsFetchingNFTs(false);
          setIsLoading(false);
          return;
        }

        const nftPromises = [];
        for (let i = 0; i < balance; i++) {
          nftPromises.push(
            (async (index) => {
              try {
                const tokenId = await retryContractCall(() =>
                  contract.tokenOfOwnerByIndex(account, index)
                );
                const tokenURI = await retryContractCall(() => contract.tokenURI(tokenId));
                let boostValue = 0;
                try {
                  if (typeof contract.getBoostValue === 'function') {
                    const rawBoost = await retryContractCall(() => contract.getBoostValue(tokenId));
                    boostValue = rawBoost.toString();
                  }
                } catch (boostErr) {
                  // Ignoring getBoostValue errors in production
                }
                let metadata;
                try {
                  let metadataUrl = tokenURI;
                  if (tokenURI.startsWith("ipfs://")) {
                    metadataUrl = tokenURI.replace("ipfs://", "https://ipfs.io/ipfs/");
                  }
                  const response = await fetch(metadataUrl);
                  metadata = await response.json();
                } catch (metaError) {
                  console.error(`Error fetching metadata for token ${tokenId}:`, metaError);
                  metadata = {
                    name: `Booster NFT #${tokenId}`,
                    description: "Metadata unavailable",
                    image: "https://via.placeholder.com/500?text=Metadata+Unavailable"
                  };
                }
                return {
                  id: tokenId.toString(),
                  tokenId: tokenId.toString(),
                  uri: tokenURI,
                  metadata,
                  boostValue
                };
              } catch (nftErr) {
                console.error(`Error processing Booster NFT at index=${index}:`, nftErr);
                return null;
              }
            })(i)
          );
        }
        const nfts = await Promise.all(nftPromises);
        const validNFTs = nfts.filter((x) => x !== null);
        setBoosterNFTs(validNFTs);
      } catch (error) {
        console.error("Error fetching Booster NFTs:", error);
      } finally {
        setIsFetchingNFTs(false);
        setIsLoading(false);
      }
    },
    [account, isFetchingNFTs, retryContractCall]
  );

  // Update total boost percentage by calling the contract's getBoost method
  const updateTotalBoost = useCallback(async () => {
    if (!account || !boosterNftContractRef.current) {
      setTotalBoostPercentage(0);
      return;
    }
    try {
      const boostFromContract = await boosterNftContractRef.current.getBoost(account);
      const boostInt = boostFromContract.toNumber();
      setTotalBoostPercentage(boostInt);
    } catch (error) {
      console.error("Error fetching boost from contract:", error);
      setTotalBoostPercentage(0);
    }
  }, [account]);

  // Update total boost whenever boosterNFTs or account changes
  useEffect(() => {
    updateTotalBoost();
  }, [boosterNFTs, account, updateTotalBoost]);

  /**
   * Mint 1 or multiple Booster NFTs.
   * Sends exactly 0.01 ETH per NFT.
   */
  const mintNFTs = useCallback(
    async (amount = 1) => {
      if (!account) {
        console.error("No account connected, cannot mint.");
        return false;
      }
      if (!isInitialized) {
        console.error("Contract not initialized, cannot mint.");
        return false;
      }
      if (amount <= 0) {
        console.error(`Invalid mint amount: ${amount}`);
        return false;
      }
      setIsMinting(true);
      try {
        // Calculate exact price in wei (0.01 ETH per NFT)
        const mintPriceWei = ethers.utils.parseUnits("0.01", 18);
        const totalPriceWei = mintPriceWei.mul(ethers.BigNumber.from(amount));

        const iface = new ethers.utils.Interface(CashBoosterNFTABI);

        let data;
        if (amount === 1) {
          data = iface.encodeFunctionData("mintBoosterNFT", []);
        } else {
          data = iface.encodeFunctionData("mintMultipleBoosterNFTs", [amount]);
        }

        const transactionParameters = {
          to: BOOSTERNFT_ADDRESS,
          from: account,
          value: totalPriceWei.toHexString(),
          data: data
        };

        const txHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [transactionParameters],
        });

        let receipt = null;
        let attempts = 0;
        const maxAttempts = 30;

        while (!receipt && attempts < maxAttempts) {
          try {
            receipt = await provider.getTransactionReceipt(txHash);
            if (!receipt) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              attempts++;
            }
          } catch (error) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempts++;
          }
        }

        if (!receipt) {
          toast.info("Transaction sent but confirmation is taking longer than expected. Check your wallet for status.");
          return true;
        }

        if (receipt.status === 0) {
          console.error("Transaction failed on-chain");
          toast.error("Transaction failed on the blockchain. The contract may be paused or have reached its maximum supply.");
          return false;
        }

        // Update NFT list
        await fetchUserBoosterNFTs();

        toast.success(`Successfully minted ${amount} Booster NFT${amount > 1 ? 's' : ''}!`);
        return true;
      } catch (error) {
        console.error("Error in mintNFTs:", error);

        if (error.code === 4001 || (error.message && error.message.includes("user rejected"))) {
          toast.info("Transaction was rejected by the user.");
        } else if (error.message && error.message.includes("insufficient funds")) {
          toast.error("Insufficient funds to complete the transaction.");
        } else if (error.message && error.message.includes("paused")) {
          toast.error("The contract is currently paused. Please try again later.");
        } else if (error.message && error.message.includes("max supply")) {
          toast.error("Maximum NFT supply has been reached.");
        } else if (error.message && error.message.includes("400")) {
          toast.error("Network error. Please try again in a few moments or refresh the page.");
        } else {
          toast.error(`Failed to mint NFT. ${error.message || "Please try again."}`);
        }

        return false;
      } finally {
        setIsMinting(false);
      }
    },
    [account, isInitialized, fetchUserBoosterNFTs, provider]
  );

  /**
   * Initialize the Booster NFT contract.
   */
  const initializeContracts = useCallback(async () => {
    if (!provider || !account || isInitializing || contractsInitializedRef.current) return;
    setIsInitializing(true);
    initializeRetryCountRef.current = 0;
    try {
      const network = await provider.getNetwork();
      if (network.chainId !== NETWORK_CONFIG.chainId) {
        setIsInitializing(false);
        return;
      }
      const code = await provider.getCode(BOOSTERNFT_ADDRESS);
      if (code === "0x") {
        setIsInitializing(false);
        return;
      }
      const signer = provider.getSigner();
      const boosterContract = new ethers.Contract(BOOSTERNFT_ADDRESS, CashBoosterNFTABI, signer);
      const codeCheck = await provider.getCode(boosterContract.address);
      if (codeCheck === "0x") {
        setIsInitializing(false);
        return;
      }
      boosterNftContractRef.current = boosterContract;
      contractsInitializedRef.current = true;
      setIsInitialized(true);
      await fetchUserBoosterNFTs(true);
    } catch (error) {
      console.error("Failed to initialize Booster NFT contract:", error);
      if (initializeRetryCountRef.current < MAX_INITIALIZE_ATTEMPTS) {
        initializeRetryCountRef.current += 1;
        const delay = Math.pow(2, initializeRetryCountRef.current) * 1000;
        setTimeout(initializeContracts, delay);
      } else {
        toast.error(`Failed to initialize Booster NFT contract: ${error.message}`, { autoClose: 5000 });
        boosterNftContractRef.current = null;
        contractsInitializedRef.current = false;
        setIsInitialized(false);
        initializeRetryCountRef.current = 0;
      }
    } finally {
      setIsInitializing(false);
    }
  }, [provider, account, isInitializing, fetchUserBoosterNFTs]);

  /**
   * Poll for NFT updates every 60 seconds.
   */
  useEffect(() => {
    if (isInitialized && account) {
      if (fetchTimeoutRef.current) clearInterval(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setInterval(() => {
        const now = Date.now();
        if (now - lastFetchTimeRef.current >= 60000) {
          fetchUserBoosterNFTs();
        }
      }, 60000);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [isInitialized, account, fetchUserBoosterNFTs]);

  /**
   * Reinitialize if provider/account/chain changes.
   */
  useEffect(() => {
    if (
      provider &&
      account &&
      chainId === NETWORK_CONFIG.chainId &&
      !isInitialized &&
      !isInitializing &&
      !contractsInitializedRef.current
    ) {
      initializeRetryCountRef.current = 0;
      initializeContracts();
    } else if ((!account || chainId !== NETWORK_CONFIG.chainId) && isInitialized) {
      setIsInitialized(false);
      contractsInitializedRef.current = false;
      boosterNftContractRef.current = null;
      setBoosterNFTs([]);
      setIsFetchingNFTs(false);
      setIsInitializing(false);
      initializeRetryCountRef.current = 0;
    }
  }, [provider, account, chainId, isInitialized, isInitializing, initializeContracts]);

  /**
   * Clean up intervals on unmount.
   */
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      boosterNFTs,
      isInitialized,
      isMinting,
      isLoading,
      fetchUserBoosterNFTs,
      mintNFTs,
      totalBoostPercentage
    }),
    [
      boosterNFTs,
      isInitialized,
      isMinting,
      isLoading,
      fetchUserBoosterNFTs,
      mintNFTs,
      totalBoostPercentage
    ]
  );

  return (
    <BoosterNFTContext.Provider value={contextValue}>
      {children}
    </BoosterNFTContext.Provider>
  );
};

export default BoosterNFTProvider;